/** The portal's automatic publish: on/off and last run (per browser), last result (per tab). */
import { useSyncExternalStore } from "react";

const ENABLED_KEY = "isyn-portal-auto-enabled";
const LAST_RUN_KEY = "isyn-portal-auto-lastrun";

const safe = <T,>(f: () => T, fallback: T): T => { try { return f(); } catch { return fallback; } };

export interface IPortalAutoResult { at: number; status: "ok" | "skipped" | "failed"; message: string }
export interface IPortalAutoState { enabled: boolean; running: boolean; result: IPortalAutoResult | null }

export const readPortalLastRun = (): number => safe(() => Number(localStorage.getItem(LAST_RUN_KEY)) || 0, 0);
export const writePortalLastRun = (at: number): void => safe(() => localStorage.setItem(LAST_RUN_KEY, String(at)), undefined);

/** OFF unless somebody switched it on in this browser. */
let state: IPortalAutoState = { enabled: safe(() => localStorage.getItem(ENABLED_KEY) === "1", false), running: false, result: null };
const listeners = new Set<() => void>();
const emit = (next: IPortalAutoState): void => { state = next; listeners.forEach((l) => l()); };

export const getPortalAutoState = (): IPortalAutoState => state;
export const setPortalAutoEnabled = (enabled: boolean): void => {
  safe(() => localStorage.setItem(ENABLED_KEY, enabled ? "1" : "0"), undefined);
  emit({ ...state, enabled });
};
export const setPortalAutoRunning = (running: boolean): void => emit({ ...state, running });
export const recordPortalAutoResult = (result: IPortalAutoResult): void => emit({ ...state, result });

export const usePortalAutoState = (): IPortalAutoState =>
  useSyncExternalStore((cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; }, getPortalAutoState, getPortalAutoState);
